import { Injectable } from '@angular/core';
import { ClothingBiometry, ClothingProfileStorekeeper, EmployeeClothingOverview } from '../../../models/clothing.model';
import { ProfileClothingUnitItem, ProfileClothingViewItem } from '../../../models/profile-clothing.model';
import {
  ClothesInfo,
  UserProfile,
  ViewUserProfileBiometryText,
  ViewUserProfileBiometryValues
} from './view-user-profile.types';
import {
  PROFILE_BIOMETRY_FIELDS,
  PROFILE_COLUMN_CLASSES,
  PROFILE_EMPTY_VALUE,
  PROFILE_EMPTY_TEXT,
  PROFILE_FIRST_CHARACTER_INDEX,
  PROFILE_PERCENT_SUFFIX,
  PROFILE_UNDEFINED_TYPE,
  PROFILE_UNKNOWN_INITIAL
} from './view-user-profile.constants';
import { mergeProfileClothingItems } from './view-user-profile-clothing.mapper';

@Injectable()
export class ViewUserProfileClothingStore {
  showStorekeeperClothing = false;
  isStorekeeperClothingLoaded = false;
  storekeeperClothingError = false;
  storekeeperClothingItems: ProfileClothingViewItem[] = [];
  clothingStorekeeper: ClothingProfileStorekeeper;
  clothingBiometry: ClothingBiometry;
  showHeadClothing = false;
  isHeadClothingLoaded = false;
  headClothingError = false;
  headClothingItems: ProfileClothingViewItem[] = [];
  combinedClothingItems: ProfileClothingViewItem[] = [];
  issueActionKey = PROFILE_EMPTY_TEXT;
  rejectRequestId = PROFILE_EMPTY_TEXT;
  extendPositionId = PROFILE_EMPTY_TEXT;
  expandedClothingCatalogItemId = PROFILE_EMPTY_TEXT;
  profileInitialsValue = PROFILE_EMPTY_TEXT;
  profileBiometryValues = this.emptyBiometryText();
  clothingBiometryValues = this.emptyBiometryText();
  infoClothes: ClothesInfo;

  private profileUser: UserProfile;

  trackByItem(index: number, item: ProfileClothingViewItem): string {
    return item.catalogItemId;
  }

  trackByUnit(index: number, unit: ProfileClothingUnitItem): string {
    return unit && unit.unitId ? unit.unitId : String(index);
  }

  showPanel(): boolean {
    return this.showStorekeeperClothing || this.showHeadClothing;
  }

  profileColumnClass(isAdmin: boolean): string {
    if (this.showStorekeeperClothing) {
      return isAdmin ? PROFILE_COLUMN_CLASSES.FULL : PROFILE_COLUMN_CLASSES.PROFILE_WITH_CLOTHING;
    }
    return PROFILE_COLUMN_CLASSES.FULL;
  }

  clothingColumnClass(isAdmin: boolean): string {
    if (this.showHeadClothing && !this.showStorekeeperClothing || isAdmin && this.showStorekeeperClothing) {
      return PROFILE_COLUMN_CLASSES.FULL;
    }
    return PROFILE_COLUMN_CLASSES.CLOTHING;
  }

  showStorekeeperContact(): boolean {
    if (this.showHeadClothing && !this.showStorekeeperClothing) {
      return false;
    }
    return !!this.clothingStorekeeper && !!this.clothingStorekeeper.userId;
  }

  showStorekeeperBiometry(): boolean {
    return this.showStorekeeperClothing && !!this.clothingBiometry;
  }

  setProfile(user: UserProfile): void {
    this.profileUser = user;
    if (!user) {
      this.profileInitialsValue = PROFILE_EMPTY_TEXT;
      this.refreshBiometryValues();
      return;
    }
    var lastInitial = user.lastName
      ? user.lastName.charAt(PROFILE_FIRST_CHARACTER_INDEX)
      : PROFILE_EMPTY_TEXT;
    var firstInitial = user.firstName
      ? user.firstName.charAt(PROFILE_FIRST_CHARACTER_INDEX)
      : PROFILE_EMPTY_TEXT;
    this.profileInitialsValue = (lastInitial + firstInitial).toUpperCase() || PROFILE_UNKNOWN_INITIAL;
    this.refreshBiometryValues();
  }

  setLegacyClothes(info: ClothesInfo): void {
    this.infoClothes = info;
  }

  clearLegacyClothes(): void {
    this.infoClothes = null;
  }

  isPanelLoaded(): boolean {
    if (this.showStorekeeperClothing && this.showHeadClothing) {
      return this.isStorekeeperClothingLoaded && this.isHeadClothingLoaded;
    }
    return this.showStorekeeperClothing
      ? this.isStorekeeperClothingLoaded
      : this.isHeadClothingLoaded;
  }

  hasPanelError(): boolean {
    if (this.showStorekeeperClothing && this.showHeadClothing) {
      return this.storekeeperClothingError || this.headClothingError;
    }
    return this.showStorekeeperClothing ? this.storekeeperClothingError : this.headClothingError;
  }

  items(): ProfileClothingViewItem[] {
    if (this.showStorekeeperClothing && this.showHeadClothing) {
      return this.combinedClothingItems;
    }
    return this.showStorekeeperClothing ? this.storekeeperClothingItems : this.headClothingItems;
  }

  hasHeadActions(item: ProfileClothingViewItem): boolean {
    if (!this.showHeadClothing || !item) {
      return false;
    }
    return this.hasUnits(item)
      ? item.units.some((unit) => !!unit && unit.canHeadExtend)
      : item.canHeadExtend;
  }

  isIssuePending(item: ProfileClothingViewItem): boolean {
    return this.issueActionKey === this.issueKey(item);
  }

  isHeadExtendPending(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): boolean {
    return !!item.positionId && this.extendPositionId === this.headExtendKey(item, unit);
  }

  isRejectPending(item: ProfileClothingViewItem): boolean {
    return !!item.requestId && this.rejectRequestId === item.requestId;
  }

  hasUnits(item: ProfileClothingViewItem): boolean {
    return !!item && !!item.units && !!item.units.length;
  }

  toggleItem(item: ProfileClothingViewItem): void {
    if (!this.hasUnits(item) && !(item && item.allUnits && item.allUnits.length)) {
      return;
    }
    this.expandedClothingCatalogItemId = this.isItemExpanded(item) ? PROFILE_EMPTY_TEXT : item.catalogItemId;
  }

  isItemExpanded(item: ProfileClothingViewItem): boolean {
    return !!item && this.expandedClothingCatalogItemId === item.catalogItemId;
  }

  unitLifeWidth(unit: ProfileClothingUnitItem): string {
    return unit.lifePercent + PROFILE_PERCENT_SUFFIX;
  }

  beginStorekeeperLoad(): void {
    this.isStorekeeperClothingLoaded = false;
    this.storekeeperClothingError = false;
  }

  beginIssue(item: ProfileClothingViewItem): void {
    this.issueActionKey = this.issueKey(item);
  }

  failIssue(): void {
    this.issueActionKey = PROFILE_EMPTY_TEXT;
  }

  beginReject(item: ProfileClothingViewItem): void {
    this.rejectRequestId = item.requestId;
  }

  failReject(): void {
    this.rejectRequestId = PROFILE_EMPTY_TEXT;
  }

  completeStorekeeperLoad(
    overview: EmployeeClothingOverview,
    items: ProfileClothingViewItem[],
    biometry: ClothingBiometry
  ): void {
    this.storekeeperClothingItems = items;
    this.clothingStorekeeper = this.resolveStorekeeper(overview);
    this.clothingBiometry = biometry;
    this.refreshBiometryValues();
    this.isStorekeeperClothingLoaded = true;
    this.storekeeperClothingError = false;
    this.showStorekeeperClothing = this.hasItems(items);
    this.expandedClothingCatalogItemId = PROFILE_EMPTY_TEXT;
    this.clearStorekeeperPendingActions();
    this.refreshCombinedItems();
  }

  failStorekeeperLoad(forbidden: boolean): void {
    this.storekeeperClothingItems = [];
    this.clothingStorekeeper = null;
    this.clothingBiometry = null;
    this.refreshBiometryValues();
    this.isStorekeeperClothingLoaded = true;
    this.clearStorekeeperPendingActions();
    this.showStorekeeperClothing = !forbidden;
    this.storekeeperClothingError = !forbidden;
    this.refreshCombinedItems();
  }

  beginHeadLoad(): void {
    this.isHeadClothingLoaded = false;
    this.headClothingError = false;
  }

  beginHeadExtension(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): void {
    this.extendPositionId = this.headExtendKey(item, unit);
  }

  failHeadExtension(): void {
    this.extendPositionId = PROFILE_EMPTY_TEXT;
  }

  completeHeadLoad(items: ProfileClothingViewItem[]): void {
    this.headClothingItems = items;
    this.clearBiometryWithoutStorekeeperPanel();
    this.isHeadClothingLoaded = true;
    this.headClothingError = false;
    this.showHeadClothing = this.hasItems(items);
    this.expandedClothingCatalogItemId = PROFILE_EMPTY_TEXT;
    this.extendPositionId = PROFILE_EMPTY_TEXT;
    this.refreshCombinedItems();
  }

  failHeadLoad(forbidden: boolean): void {
    this.headClothingItems = [];
    this.clearBiometryWithoutStorekeeperPanel();
    this.isHeadClothingLoaded = true;
    this.extendPositionId = PROFILE_EMPTY_TEXT;
    this.showHeadClothing = !forbidden;
    this.headClothingError = !forbidden;
    this.refreshCombinedItems();
  }

  private clearStorekeeperPendingActions(): void {
    this.issueActionKey = PROFILE_EMPTY_TEXT;
    this.rejectRequestId = PROFILE_EMPTY_TEXT;
  }

  private clearBiometryWithoutStorekeeperPanel(): void {
    if (!this.showStorekeeperClothing) {
      this.clothingStorekeeper = null;
      this.clothingBiometry = null;
      this.refreshBiometryValues();
    }
  }

  private hasItems(items: ProfileClothingViewItem[]): boolean {
    return !!items && !!items.length;
  }

  private refreshCombinedItems(): void {
    this.combinedClothingItems = mergeProfileClothingItems(
      this.storekeeperClothingItems,
      this.headClothingItems
    );
  }

  private issueKey(item: ProfileClothingViewItem): string {
    return item.requestId || item.catalogItemId;
  }

  private headExtendKey(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): string {
    return unit && unit.unitId ? unit.unitId : item.positionId;
  }

  private resolveStorekeeper(overview: EmployeeClothingOverview): ClothingProfileStorekeeper {
    return overview && overview.storekeeper && overview.storekeeper.userId
      ? overview.storekeeper
      : null;
  }

  private refreshBiometryValues(): void {
    this.profileBiometryValues = this.biometryText(true);
    this.clothingBiometryValues = this.biometryText(false);
  }

  private biometryText(useProfileFallback: boolean): ViewUserProfileBiometryText {
    return {
      growth: this.biometryTextValue(PROFILE_BIOMETRY_FIELDS.GROWTH, useProfileFallback),
      weight: this.biometryTextValue(PROFILE_BIOMETRY_FIELDS.WEIGHT, useProfileFallback),
      clothingSize: this.biometryTextValue(PROFILE_BIOMETRY_FIELDS.CLOTHING_SIZE, useProfileFallback),
      shoeSize: this.biometryTextValue(PROFILE_BIOMETRY_FIELDS.SHOE_SIZE, useProfileFallback)
    };
  }

  private biometryTextValue(field: string, useProfileFallback: boolean): string {
    var value = this.biometryField(this.clothingBiometry, field);
    if (!this.hasMetricValue(value) && useProfileFallback && this.profileUser) {
      value = this.biometryField(this.profileUser.parametersUser, field);
    }
    return this.hasMetricValue(value) ? String(value) : PROFILE_EMPTY_VALUE;
  }

  private emptyBiometryText(): ViewUserProfileBiometryText {
    return {
      growth: PROFILE_EMPTY_VALUE,
      weight: PROFILE_EMPTY_VALUE,
      clothingSize: PROFILE_EMPTY_VALUE,
      shoeSize: PROFILE_EMPTY_VALUE
    };
  }

  private biometryField(source: ViewUserProfileBiometryValues, field: string): string | number {
    if (!source) {
      return null;
    }
    switch (field) {
      case PROFILE_BIOMETRY_FIELDS.GROWTH: return source.growth;
      case PROFILE_BIOMETRY_FIELDS.WEIGHT: return source.weight;
      case PROFILE_BIOMETRY_FIELDS.CLOTHING_SIZE: return source.clothingSize;
      case PROFILE_BIOMETRY_FIELDS.SHOE_SIZE: return source.shoeSize;
      default: return null;
    }
  }

  private hasMetricValue(value: string | number): boolean {
    return value !== null && typeof value !== PROFILE_UNDEFINED_TYPE && value !== PROFILE_EMPTY_TEXT;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/view-user-profile-clothing.store.ts